import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tray, ArrowsClockwise, Factory, Check, Bug, CheckCircle, Wrench, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const ICON = { indent_converted: ArrowsClockwise, indent_production: Factory, issue_new: Bug, issue_resolved: CheckCircle, issue_progress: Wrench, issue_reopened: ArrowCounterClockwise };
const ago = (s) => { const m = Math.round((Date.now() - new Date(s)) / 60000); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`; };

export default function NotificationBell() {
  const [d, setD] = useState({ rows: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const load = useCallback(() => api.get("/notifications").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (!d.rows.length) return null;
  const openOne = async (n) => { setOpen(false); if (!n.read_at) api.post(`/notifications/${n.id}/read`).then(load).catch(() => {}); if (n.link) navigate(n.link); };
  const readAll = () => api.post("/notifications/read-all").then(load).catch(() => {});
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative h-8 w-8 flex items-center justify-center rounded-md text-foreground hover:bg-muted transition-colors" title="Notifications">
          <Tray size={18} weight="bold" />
          {d.unread > 0 && <span data-testid="notif-bell-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">{d.unread > 99 ? "99+" : d.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="notif-panel">
        <div className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
          <div><div className="text-sm font-semibold">Notifications</div><div className="text-xs text-muted-foreground">{d.unread} unread</div></div>
          {d.unread > 0 && <button onClick={readAll} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid="notif-read-all"><Check size={12} /> Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {d.rows.map((n) => { const Icon = ICON[n.kind] || Tray; return (
            <button key={n.id} onClick={() => openOne(n)} className={`w-full text-left flex gap-3 px-4 py-3 text-sm border-b last:border-b-0 hover:bg-primary/5 transition-colors ${n.read_at ? "opacity-60" : ""}`} data-testid={`notif-item-${n.id}`}>
              <Icon size={16} className="text-primary mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[12px] font-semibold leading-tight">{n.title}</div>
                {n.body && <div className="text-[11px] text-muted-foreground mt-0.5">{n.body}</div>}
                <div className="font-mono text-[10px] text-muted-foreground mt-1">{ago(n.created_at)}</div>
              </div>
              {!n.read_at && <span className="ml-auto mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
            </button>
          ); })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
